import React from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Users, DollarSign, Video, ShoppingBag, BookOpen, Check, X, Upload, TrendingUp, Crown, Lock, Unlock, Settings, RefreshCw, Globe, Edit3, FileSpreadsheet, AlertCircle, Download, Youtube, Plus, Layers, Calendar, Flag, Star, Trash2, Gift, Ban, UserX, UserCheck, CheckCircle, ShieldCheck, Clock, PartyPopper, Archive } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Revenue Dashboard tab.
 *
 * Lifted out of AdminPanel.js during the Phase 2 frontend split (Wave 4).
 * Pure presentational — receives parent state + handlers via props.
 */
export default function CommissionDashboardTab(props) {
  const { user, navigate, getAuthHeader, fetchAllData, fileInputRef, users, setUsers, userSearch, setUserSearch, filteredUsers, pendingFounders, founderSettings, setFounderSettings, founderSeatsRemaining, updateFounderSettings, toggleFounderEnrollment, toggleFoundingMember, suspendUser, archiveUser, permanentlyDeleteUser, changeUserTier, sales, pendingSales, pendingListingSales, handleVerifySale, handleVerifyListingSale, handleUnverifyListingSale, handleCancelListingSale, commissionDashboard, promoCodes, newPromo, setNewPromo, createPromoCode, deletePromoCode, promoToDelete, setPromoToDelete, potData, setPotData, updatePot, advertisers, pendingVideos, pendingLibraryVideos, handleBatchApproveVideos, handleVideoReview, handleLibraryVideoReview, moveDownlineData, setMoveDownlineData, featuredMembers, selectedUserToFeature, setSelectedUserToFeature, featuredText, setFeaturedText, handleAddFeaturedMember, handleRemoveFeaturedMember, tierFeatures, editingTier, setEditingTier, editFeatures, setEditFeatures, startEditingTier, saveTierFeatures, customTiers, showTierForm, setShowTierForm, newTier, setNewTier, tierFeaturesInput, setTierFeaturesInput, handleCreateTier, handleDeleteTier, pendingServices, flaggedServices, handleApproveService, handleFlagService, handleDeleteService, edirectoryStatus, syncingEdir, syncEdirectory, importedTransactions, importing, importResult, handleFileSelect, downloadSampleCSV } = props;

  return (
    <div className="space-y-4" data-testid="commission-dashboard-tab">
      <h2 className="text-2xl font-playfair font-bold text-foreground mb-1">Revenue & Commission Dashboard</h2>
      <p className="text-sm text-muted-foreground mb-4" data-testid="commission-source-note">
        Commission is earned on <span className="text-foreground font-medium">Directory listing sales only</span> — membership subscriptions do not generate commission.
      </p>
            
                  {commissionDashboard ? (
                    <>
                      <div className="grid md:grid-cols-4 gap-4">
                        <div className="wm-card p-4 rounded-lg text-center">
                          <p className="text-sm text-muted-foreground">Total Revenue</p>
                          <p className="text-2xl font-bold text-foreground">${commissionDashboard.total_revenue.toLocaleString()}</p>
                        </div>
                        <div className="wm-card p-4 rounded-lg text-center">
                          <p className="text-sm text-muted-foreground">Platform Commission</p>
                          <p className="text-2xl font-bold text-emerald-400">${commissionDashboard.platform_commission.toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground mt-1">{commissionDashboard.settings.we_millionaires_commission}%</p>
                        </div>
                        <div className="wm-card p-4 rounded-lg text-center">
                          <p className="text-sm text-muted-foreground">Member Payouts</p>
                          <p className="text-2xl font-bold text-blue-400">${commissionDashboard.member_commission.toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground mt-1">{commissionDashboard.settings.member_commission}%</p>
                        </div>
                        <div className="wm-card p-4 rounded-lg text-center">
                          <p className="text-sm text-muted-foreground">Upline Payouts</p>
                          <p className="text-2xl font-bold text-amber-400">${commissionDashboard.downline_pool.toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground mt-1">{commissionDashboard.settings.upline_commission || commissionDashboard.settings.downline_commission}%</p>
                        </div>
                      </div>
                
                      <div className="wm-card p-6 rounded-lg">
                        <h3 className="font-semibold text-foreground mb-4">
                          Recent Verified Listing Sales ({commissionDashboard.total_transactions} credited
                          {commissionDashboard.pending_verification ? ` · ${commissionDashboard.pending_verification} pending verification` : ''})
                        </h3>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-border">
                                <th className="text-left py-2 text-muted-foreground">Date</th>
                                <th className="text-left py-2 text-muted-foreground">User</th>
                                <th className="text-left py-2 text-muted-foreground">Type</th>
                                <th className="text-right py-2 text-muted-foreground">Amount</th>
                                <th className="text-right py-2 text-muted-foreground">Platform</th>
                                <th className="text-right py-2 text-muted-foreground">Member</th>
                              </tr>
                            </thead>
                            <tbody>
                              {commissionDashboard.recent_commissions.map((c, i) => (
                                <tr key={i} className="border-b border-border/30">
                                  <td className="py-2 text-muted-foreground">{c.date ? new Date(c.date).toLocaleDateString() : '—'}</td>
                                  <td className="py-2 text-foreground">{c.user_email || '—'}</td>
                                  <td className="py-2"><span className="text-xs px-2 py-0.5 rounded bg-primary/20 text-primary capitalize">{c.tier}</span></td>
                                  <td className="py-2 text-right text-foreground">${c.amount.toFixed(2)}</td>
                                  <td className="py-2 text-right text-emerald-400">${c.platform_commission.toFixed(2)}</td>
                                  <td className="py-2 text-right text-blue-400">${c.member_commission.toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="wm-card p-8 rounded-lg text-center">
                      <p className="text-muted-foreground">Loading commission data...</p>
                    </div>
                  )}
    </div>
  );
}
